import React, { useEffect, useMemo, useState } from 'react';
import { useCommands } from '../api/commands';
import PageHeader from '../components/PageHeader';

function normVehicle(v) {
  // tolerate both unified & legacy fields
  const reg   = v.reg || v.numberPlate || '';
  const make  = v.make || v.Make || '';
  const model = v.model || v.Model || '';
  const status = v.status || v.Status || 'Unknown';
  const classification = v.classification || v.Classification || '';
  const currentLocation = v.currentLocation || v.location || '';
  const lastEventAt = v.lastEventAt || v.lastUpdate || '';

  return {
    ...v,
    reg,
    make,
    model,
    status,
    classification,
    currentLocation,
    lastEventAt,
  };
}

export default function Vehicles() {
  const cmd = useCommands();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [classFilter, setClassFilter] = useState('all');
  const [page, setPage] = useState(1);
  const pageSize = 25;

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setError('');
      try {
        // unified: GET /fleet/vehicles (via commands prefix)
        const data = await (cmd.fleet?.vehicles?.list
          ? cmd.fleet.vehicles.list()
          : cmd.vans.list()); // fallback if commands not updated yet
        const normalized = Array.isArray(data) ? data.map(normVehicle) : [];
        if (alive) setRows(normalized);
      } catch (e) {
        if (alive) setError(e?.response?.data?.error || e?.message || 'Failed to load vehicles');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [cmd]);

  const statuses = useMemo(() => {
    const u = new Set(rows.map(v => v.status || '—'));
    return ['all', ...Array.from(u).sort((a,b)=>a.localeCompare(b))];
  }, [rows]);

  const classes = useMemo(() => {
    const u = new Set(rows.map(v => v.classification || '—'));
    return ['all', ...Array.from(u).sort((a,b)=>a.localeCompare(b))];
  }, [rows]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return rows
      .filter(v => {
        if (statusFilter !== 'all' && (v.status || '—') !== statusFilter) return false;
        if (classFilter  !== 'all' && (v.classification || '—') !== classFilter) return false;
        if (!ql) return true;
        const hay = [
          v.reg, v.numberPlate, v.make, v.model, v.status, v.currentLocation,
          v.classification, v.lastEventAt
        ].filter(Boolean).join(' ').toLowerCase();
        return hay.includes(ql);
      })
      .sort((a,b)=> (a.reg||'').localeCompare(b.reg||''));
  }, [rows, q, statusFilter, classFilter]);

  useEffect(()=>{ setPage(1); }, [q, statusFilter, classFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = filtered.slice((page-1)*pageSize, page*pageSize);

  return (
    <div className="container">
      <PageHeader eyebrow="Fleet" title="Vehicles" />

      {error && <div className="page-alerts"><div className="alert error">{error}</div></div>}

      <div className="card">
        <div className="toolbar mb-16">
          <input
            className="input grow"
            placeholder="Search vehicles"
            value={q}
            onChange={e=>setQ(e.target.value)}
          />

          <select
            className="input"
            value={statusFilter}
            onChange={e=>setStatusFilter(e.target.value)}
          >
            {statuses.map(s => <option key={s} value={s}>{s==='all' ? 'All statuses' : s}</option>)}
          </select>

          <select
            className="input"
            value={classFilter}
            onChange={e=>setClassFilter(e.target.value)}
          >
            {classes.map(c => <option key={c} value={c}>{c==='all' ? 'All classifications' : c}</option>)}
          </select>

          {(q || statusFilter !== 'all' || classFilter !== 'all') && (
            <button className="btn ghost" onClick={()=>{ setQ(''); setStatusFilter('all'); setClassFilter('all'); }}>
              Clear filters
            </button>
          )}
        </div>

        {loading ? <div className="empty">Loading</div> : filtered.length === 0 ? (
          <div className="empty">No vehicles found.</div>
        ) : (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Reg</th>
                    <th>Make and model</th>
                    <th>Classification</th>
                    <th>Status</th>
                    <th>Location</th>
                    <th>Last update</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((v, i) => (
                    <tr key={v.id || v.reg || v.numberPlate || i}>
                      <td className="cell-strong nowrap">{v.reg || v.numberPlate || '—'}</td>
                      <td>{(v.make || '—') + (v.model ? ` ${v.model}` : '')}</td>
                      <td>{v.classification || v.class || '—'}</td>
                      <td><span className="badge">{v.status || '—'}</span></td>
                      <td>{v.currentLocation || '—'}</td>
                      <td className="cell-muted nowrap">{v.lastEventAt || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pager">
              <span className="pager-info">
                Page {page} of {totalPages}, showing {pageRows.length} of {filtered.length}
              </span>
              <div className="pager-buttons">
                <button className="btn secondary" disabled={page<=1} onClick={()=>setPage(p=>p-1)}>Previous</button>
                <button className="btn secondary" disabled={page>=totalPages} onClick={()=>setPage(p=>p+1)}>Next</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
